import {
  Badge,
  CiudadModel,
  ComunaModel,
  Feature,
  ProjectImage,
  RegionModel,
  Tag,
  User
} from '@portalterreno/backend-legacy';
import {
  Campaign,
  Project as ProjectModel
} from '@portalterreno/backend-legacy-campaigns';
import { PortalTerrenoTheme } from '@portalterreno/react-ui-themes';
import { hasCookie, setCookie } from 'cookies-next';
import moment from 'moment';
import { RowDataPacket } from 'mysql2';
import { GetServerSideProps, NextPage } from 'next';
import Head from 'next/head';
import { useTranslation } from 'next-i18next';
import React from 'react';
import { Op } from 'sequelize';

import ProjectPage, {
  ProjectPageProps
} from '../../../../../components/Property/ProjectPage';
import fetchMapboxPreview from '../../../../../lib/fetchMapboxPreview';
import getRelatedProperties from '../../../../../lib/fetchRelatedProjects';
import PropertyViewTracker from '../../../../../lib/trackers/propertyViewTracker';
import PropertyProvider, {
  PropertyFeature,
  PropertyProviderProps
} from '../../../../../providers/PropertyProvider';
import { Pricing, RelatedProperty, SetAllNullable } from '../../../../../types';
import { pool } from '../../../../../utils/database';
import fetchPricing from '../../../../../utils/fetchPricing';
import getCurrencies from '../../../../../utils/getCurrencies';
import getProjectUrl from '../../../../../utils/getProjectUrl';
import removeHtmlEntities from '../../../../../utils/removeHtmlEntities';

type Photo = PropertyProviderProps['photos'][0];
type Photos = Array<Photo>;
type Color = keyof PortalTerrenoTheme['palette']['colors'];

export type ProjectSheetProps = Omit<
  PropertyProviderProps,
  'operationFormatted' | 'modifiedAt'
> &
  Pick<ProjectPageProps, 'badges'>;

const ProjectSheet: NextPage<ProjectSheetProps> = (props) => {
  const { t } = useTranslation('common');
  const strippedDescription =
    props.description?.replace(/(<([^>]+)>)/gi, '') || '';
  const description = removeHtmlEntities(strippedDescription);

  const descriptionProperty = props.description.replaceAll('\\n', '<br />');

  const projectUrl = getProjectUrl({
    id: props.id,
    commune: props.communeName,
    city: props.cityName,
    title: props.title
  });

  return (
    <PropertyProvider
      value={{
        ...props,
        modifiedAt: '',
        operationFormatted: '',
        latitude: props.latitude || undefined,
        longitude: props.longitude || undefined,
        zoom: props.zoom || undefined,
        description: descriptionProperty
      }}
    >
      <Head>
        <title key='pageTitle'>{`${props.title} | PortalTerreno`}</title>
        <meta
          property='og:title'
          content={`${props.title} - ${t('common:ogDescriptionProperty')}`}
          key='title'
        />
        <meta
          property='og:description'
          content={`${description.substring(0, 150)}...`}
          key='ogDescription'
        />
        <meta
          property='og:image'
          content={
            props.thumbnail
              ? `https://www.portalterreno.com/img-thumb.php?w=300&h=300&src=https://www.portalterreno.com/imagenes/proyectos/${props.thumbnail}`
              : props.photoCover
          }
          key='image'
        />
        <meta
          name='description'
          content={`${description.substring(0, 200)}`}
          key='description'
        />
        <meta
          property='og:url'
          content={`${process.env.NEXT_PUBLIC_CANONICAL_HOST}${projectUrl}`}
          key='url'
        />
        <meta
          name='Keywords'
          content={`Terrenos, proyecto, ${props.title}, ${props.communeName}, terrenos en ${props.communeName}`}
          key='keywords'
        />
        <link
          rel='canonical'
          href={`${process.env.NEXT_PUBLIC_CANONICAL_HOST}${projectUrl}`}
          key='canonical'
        />
      </Head>
      <ProjectPage badges={props.badges} />
    </PropertyProvider>
  );
};

export const getServerSideProps: GetServerSideProps<
  SetAllNullable<ProjectSheetProps>
> = async (context) => {
  if (!context.params || !context.params.idSlug || !context.params.location) {
    return {
      notFound: true
    };
  }
  if (!hasCookie('user', { req: context.req, res: context.res })) {
    setCookie(
      'user',
      { name: '', email: '', phone: '' },
      { req: context.req, res: context.res, maxAge: 2592000 }
    );
  }

  const [projectId] = (context.params.idSlug as string).split('-');

  const numericId = Number(projectId);

  if (!numericId || Number.isNaN(numericId)) {
    return {
      notFound: true
    };
  }

  const now = new Date();

  const project = await ProjectModel.findOne({
    attributes: [
      'id',
      'arreglo_mapa',
      'aspectos_destacables',
      'descripcion',
      'fecha_modificacion',
      'img_map',
      'logo_empresa',
      'logo_proyecto',
      'mapa_dron',
      'master_plan',
      'nombre',
      'propietario',
      'superficie',
      'val_superficie',
      'val_tipo_superficie',
      'val_valor',
      'vende',
      'foto_cover',
      'thumbnail',
      'cyber_chip',
      'cyberday',
      'numero',
      'tarjeta_contacto',
      'texto_cuotas',
      'texto_descuento',
      'tipo_valor',
      'tipo_video',
      'valor_cuota',
      'valor_descuento',
      'video',
      'descripcion_cyber',
      'whatsapp_activo',
      'usuario_id',
      'ver_telefono'
    ],
    include: [
      {
        model: CiudadModel,
        attributes: ['id', 'nombre'],
        required: true
      },
      {
        model: ComunaModel,
        attributes: ['id', 'nombre'],
        required: true
      },
      {
        model: RegionModel,
        attributes: ['id', 'nombre'],
        required: true
      },
      {
        model: User,
        attributes: ['id', 'email', 'whatsapp', 'telefono'],
        required: true
      },
      {
        model: Tag,
        as: 'tags',
        attributes: ['nombre'],
        through: {
          attributes: [],
          where: {
            activo: true
          }
        },
        where: {
          activo: true
        },
        required: false
      },
      {
        model: Badge,
        as: 'badges',
        attributes: ['logo', 'name', 'description', 'border_color'],
        through: {
          attributes: []
        },
        required: false
      },
      {
        model: ProjectImage,
        attributes: ['archivo', 'posicion'],
        required: false
      },
      {
        model: Feature,
        attributes: ['icon', 'name', 'description', 'is_highlighted'],
        through: {
          attributes: ['value']
        },
        required: false
      },
      {
        model: Campaign,
        required: false,
        attributes: ['id', 'card_title', 'logo'],
        through: {
          attributes: []
        },
        where: {
          end_date: {
            [Op.gte]: now
          },
          start_date: {
            [Op.lte]: now
          }
        }
      }
    ],
    where: {
      id: numericId,
      activo: true
    },
    order: [
      [ProjectImage, 'posicion', 'ASC'],
      [Feature, 'name', 'ASC']
    ]
  });

  if (!project) {
    return {
      notFound: true
    };
  }

  await PropertyViewTracker.track({ projectId: numericId }, context);

  let sellerPhone: string | null = null;

  if (project.ver_telefono && project.user.telefono) {
    sellerPhone = project.user.telefono;
  }

  const photos: Photos = Object.assign(
    // TODO: replace the photo of the placeholder for the not found image
    new Array(5).fill({ url: 'https://via.placeholder.com/500', label: '' }),
    project.proyecto_imagenes.map<Photo>((image) => {
      let url: string;

      if (image.archivo.startsWith('http')) {
        url = image.archivo;
      } else {
        url = `https://www.portalterreno.com/imagenes/proyectos/${image.archivo}`;
      }

      return {
        src: url,
        alt: ''
      };
    })
  );

  let latitude: null | number = null;
  let longitude: null | number = null;
  let zoom: null | number = null;

  if (project.arreglo_mapa) {
    try {
      const [data] = JSON.parse(project.arreglo_mapa);

      if (data && data.type === 'MARKER') {
        [latitude, longitude] = data.geometry;
      }
      zoom = data.zoom ?? null;
    } catch (e) {}
  }

  const { principalCurrency, secondaryCurrency } = getCurrencies(
    project.tipo_valor || 'pesos'
  );

  const pricing = await fetchPricing(project.val_valor || 0, principalCurrency);

  let masterPlanUrl: null | string = null;

  if (project.master_plan) {
    masterPlanUrl = `https://www.portalterreno.com/imagenes/proyectos/${project.master_plan}`;
  }

  let relatedProperties: Array<RelatedProperty> = [];

  try {
    relatedProperties = await getRelatedProperties(numericId, 16);
  } catch (e) {
    console.error(e);
  }

  let surfaceUnit = project.val_tipo_superficie || project.val_superficie;
  let surface = Number(project.superficie);

  if (surfaceUnit && surfaceUnit.toLowerCase().includes('h')) {
    surfaceUnit = 'Ha';
    surface = surface / 10000;
  } else {
    surfaceUnit = 'm²';
  }

  const photoCover = `https://www.portalterreno.com/imagenes/proyectos/${project.foto_cover}`;
  const directCredit = project.tags.length > 0;

  // Default info for proyects badge
  const campaignBackgroundColor: Color = 'white';
  const campaignIconColor: Color = 'dodgerBlue';
  const campaignTextColor: Color = 'midnightBlue';

  // cyberday info
  const [options] = await pool.query<Array<RowDataPacket>>(
    `
      SELECT
        Opcion.nombre,
        Opcion.valor
      FROM opciones AS Opcion
      WHERE
        Opcion.nombre IN ("fecha_inicio_campana", "fecha_termino_campana")
    `
  );

  const campaignStartDate = moment(
    options.find((option) => option.nombre === 'fecha_inicio_campana')?.valor
  );

  const campaignEndDate = moment(
    options.find((option) => option.nombre === 'fecha_termino_campana')?.valor
  );

  let campaignPricing: Pricing | null = null;
  let campaignChipText: string | null = null;
  let campaignDescription: string | null = null;
  let campaignDiscountText: string | null = null;
  const todayDate = moment();
  if (
    todayDate >= campaignStartDate &&
    todayDate <= campaignEndDate &&
    project.cyberday === 1
  ) {
    if (project.valor_descuento) {
      campaignPricing = await fetchPricing(
        Number(project.valor_descuento),
        principalCurrency
      );
    }
    campaignChipText = project.cyber_chip || null;
    campaignDescription = project.descripcion_cyber || null;
    campaignDiscountText = project.texto_descuento || null;
  }

  let mapPreview = project.img_map;

  if (!mapPreview && latitude && longitude) {
    mapPreview = await fetchMapboxPreview({ latitude, longitude });

    await pool.query('UPDATE proyectos SET img_map = ? WHERE id = ?', [
      mapPreview,
      numericId
    ]);
  }

  if (!latitude || !longitude) {
    mapPreview = `${process.env.NEXT_PUBLIC_IMAGES_HOST}/maps/no_map.png`;
  }

  // legacy maps
  if (mapPreview && !mapPreview.startsWith('http')) {
    mapPreview = `https://www.portalterreno.com/imagenes/terrenos_map/${project.img_map}`;
  }

  let sellerWhatsapp: string | undefined;

  if (project.user.whatsapp && project.whatsapp_activo) {
    sellerWhatsapp = project.user.whatsapp;
  }

  try {
    await pool.query(
      `UPDATE proyectos
        SET visto =
          CASE
            WHEN visto IS NOT NULL THEN visto + 1
            ELSE 1
          END
        WHERE id = ?`,
      [numericId]
    );
  } catch (e) {
    console.error(e);
  }

  let videoProvider: ProjectSheetProps['videoProvider'] | null = null;

  if (project.tipo_video === 'youtube' || project.tipo_video === 'vimeo') {
    videoProvider = project.tipo_video;
  }

  const highlightedFeatures: Array<PropertyFeature> = [];
  const nonHighlightedFeatures: Array<PropertyFeature> = [];

  for (const feature of project.features) {
    const propertyFeature: PropertyFeature = {
      icon: feature.icon,
      name: feature.name,
      value: feature.project_feature.value,
      description: feature.description
    };

    if (feature.is_highlighted) {
      highlightedFeatures.push(propertyFeature);
    } else {
      nonHighlightedFeatures.push(propertyFeature);
    }
  }

  return {
    props: {
      id: numericId,
      cityId: project.ciudad.id,
      cityName: project.ciudad.nombre,
      communeId: project.comuna.id,
      communeName: project.comuna.nombre,
      description: project.descripcion,
      highlights: project.aspectos_destacables,
      latitude,
      propertyLogo: `https://www.portalterreno.com/imagenes/logo_proyectos/${project.logo_proyecto}`,
      companyLogo: project.logo_empresa
        ? `https://www.portalterreno.com/imagenes/proyectos/${project.logo_empresa}`
        : `${process.env.NEXT_PUBLIC_IMAGES_HOST}/properties/no_logo.png`,
      longitude,
      mapPreview,
      zoom,
      ownerName: project.propietario,
      videoId: project.video,
      videoProvider,
      photos: [
        {
          src: photoCover,
          alt: ''
        } as Photo,
        ...photos
      ],
      photoCover,
      directCredit,
      price: project.val_valor,
      pricing,
      principalCurrency,
      modifiedDate: project.fecha_modificacion?.toISOString() || null,
      regionId: project.region.id,
      regionName: project.region.nombre,
      secondaryCurrency,
      sellerName: project.vende || null,
      surface,
      surfaceUnit,
      title: project.nombre,
      thumbnail: project.thumbnail,
      virtualTourUrl: project.mapa_dron,
      masterPlanUrl,
      relatedProperties,
      page: 'projects',
      videoUrl: project.video || null,
      videoPlatform: project.tipo_video || null,
      sellerEmail: project.user.email || 'contacto@portalterreno.com',
      sellerPhone,
      sellerWhatsapp: sellerWhatsapp || null,
      campaignChipText,
      campaignDescription,
      campaignDiscountText,
      campaignPricing,
      campaignBackgroundColor,
      campaignIconColor,
      campaignTextColor,
      operation: 'comprar',
      propertyType: 'project',
      totalResultsPerPage: 0,
      totalPages: 0,
      totalResults: 0,
      badges: project.badges.map((badge) => ({
        logo: badge.logo,
        name: badge.name,
        description: badge.description,
        borderColor: badge.border_color
      })),
      features: nonHighlightedFeatures,
      highlightedFeatures,
      projectCampaigns: project.campaigns.map((campaign) => ({
        id: campaign.id,
        card_title: campaign.card_title,
        logo: campaign.logo
      }))
    }
  };
};

export default ProjectSheet;
